import { useI18n } from 'vue-i18n';
import { usePersistentState } from '~/composables/usePersistentState'

export const useLanguage = () => {
	const { t, tm, locale } = useI18n();

	const langRadio = usePersistentState<string>('app-locale', () => locale.value)


	/**
	 * 언어팩에서 폼 라벨 가져오기
	 * @param key	코드 키
	 */
	const getLabel = (key: string): string => {
		return t(`labels.${key}`);
	}

	/**
	 * 언어팩에서 폼 플레이스홀더 가져오기
	 * @param key	코드 키
	 * @param params	파라미터 치환용 객체 (예: {min: 5, max: 10} 등)
	 */
	const getPlaceholder = (key: string, params?: Record<string, any>): string => {
		const result = t(`placeholders.${key}`, params || {});
		return result === `placeholders.${key}` ? '' : result;
	};
	// const getPlaceholder = (key: string): string => {
	// 	return t(`placeholders.${key}`);
	// }


	/**
	 * 검증 메시지 가져오기(파라미터 치환 포함)
	 * @param category	검증 카테고리 (예: 'required', 'format', 'length' 등)
	 * @param key		코드 키
	 * @param params	메시지 파라미터 (예: {min}, {max} 등)
	 */
	const getValidationMessage = (category: string, key: string, params?: Record<string, any>): string => {
		return t(`validation.${category}.${key}`, params ?? {});
	}

	/**
	 * 버튼 텍스트 가져오기
	 * @param key	코드 키
	 */
	const getButtonText = (key: string): string => {
		return t(`buttons.${key}`) || key;
	}

	/*
	 * 공통 텍스트 가져오기
	 */
	const getCommonText = (category: string, key: string): string => {
		return t(`common.${category}.${key}`) || key;
	}

	/**
	 * 메시지 가져오기
	 * @param category	메시지 카테고리 (예: 'error', 'info' 등)
	 * @param key	메시지 키
	 */
	const getMessage = (category: string, key: string): string => {
		return t(`messages.${category}.${key}`) || '';
	}

	/**
	 * 폼 옵션 텍스트 가져오기
	 * @param key 코드 키
	 */
	const getOption = (key: string): string => {
		return t(`options.${key}`);
	}

	const getPageOptions = (key: string): string => {
		return t(`page.${key}`);
	}

	const getConstantOptions = (key: string): string => {
		return t(`constant.${key}`);
	}

	const getNavigation = (key: string | number | undefined | null): string => {
		if (key === undefined || key === null || key === 0) {
			return '-';
		}

		const result = t(`navigation.${key}`);
		return result === `navigation.${key}` ? '-' : result;
	};

	/** 회원관리 텍스트 */
	const getUserText = (key: string): string => {
		return t(`user.${key}`) || key;
	}

	/** 메뉴권한관리 텍스트 */
	const getMenuText = (key: string): string => {
		return t(`menuAuth.${key}`) || key;
	}

	/** 지도 관련 */
	const getMapText = (key: string): string => {
		const result = t(`map.${key}`);
		return result === `map.${key}` ? key : result;
	};

	/** 그룹관리 텍스트 */
	const getGroupText = (key: string): string => {
		return t(`group.${key}`) || key;
	}

	/** 공지관리 텍스트 */
	const getBoardText = (key: string): string => {
		return t(`board.${key}`) || key;
	}

	/** 약관관리 텍스트 */
	const getTermsText = (key: string): string => {
		return t(`terms.${key}`) || key;
	}

	/** 로그관리 텍스트 */
	const getLogText = (key: string): string => {
		return t(`log.${key}`) || key;
	}

	/** 현장관리 텍스트 */
	const getFacilityText = (key: string): string => {
		return t(`facility.${key}`) || key;
	}

	/** 장비관리 텍스트 */
	const getTerminalText = (key: string): string => {
		return t(`terminal.${key}`) || key;
	}

	/** as관리 텍스트 */
	const getASText = (key: string): string => {
		return t(`warranty.${key}`) || key;
	}

	/** 일반 텍스트 */
	const getText = (key: string): string => {
		return t(`text.${key}`) || key;
	}

	/** 내비게이션(바로가기) 관련 텍스트 */
	const getDirectText = (key: string): string => {
		const text = t(`navigation.direct.${key}`) || key;
		// | 문자를 줄바꿈으로 변환
		return text.replace(/\|/g, '\n');
	}

	/** 상태 관련 텍스트 */
	const getStatusText = (key: string): string => {
		return t(`status.${key}`) || key;
	}

	/** 상태 관련 텍스트 */
	const getFAQText = (key: string): string => {
		return t(`faq.${key}`) || key;
	}

	/** 에러 메시지 가져오기 */
	const getErrorText = (key: string): string => {
		return t(`error.${key}`) || key;
	}

	/** SD카드 메시지 가져오기 */
	const getSdCardText = (
		key: string,
		params: (string | number)[] = []
	): string => {
		const fullKey = `sdCard.${key}`;
		// params가 있으면 i18n에 넘겨서 {0}, {1}… 자동 치환
		const result = params.length > 0
		? t(fullKey, params)
		: t(fullKey);
	
		// 번역문이 없다면 key 그대로 리턴
		return result === fullKey ? key : result;
  	};

	/**
	 * 시간 단위 텍스트 반환 (예: '01시', '01 hour')
	 */
	const getHourUnit = (hour: string | number): string => {
		return t('common.hourUnit', { hour });
	}

	/**
	 * 분 단위 텍스트 반환 (예: '01분', '01 minute')
	 */
	const getMinuteUnit = (minute: string | number): string => {
		return t('common.minuteUnit', { minute });
	}

	const lang = {
		// 폼 라벨
		label: getLabel,
		placeholder: getPlaceholder,
		option: getOption,
		text: getText,
		constant: getConstantOptions,
		page: getPageOptions,

		// 검증 메시지
		validation: {
			required: (key: string) => getValidationMessage('required', key),
			format: (key: string) => getValidationMessage('format', key),
			length: (key: string, params?: Record<string, any>) => getValidationMessage('length', key, params),
			match: (key: string) => getValidationMessage('match', key),
			duplicate: (key: string) => getValidationMessage('duplicate', key),
			terms: (key: string) => getValidationMessage('terms', key),
		},

		// 버튼
		button: getButtonText,

		// 공통
		title: (key: string) => getCommonText('title', key),
		action: (key: string) => getCommonText('action', key),
		unit: (key: string) => getCommonText('unit', key),

		// 메시지 (구조화된 messages 섹션)
		messages: {
			confirm: (key: string) => getMessage('confirm', key),
			completion: (key: string) => getMessage('completion', key),
			verification: (key: string) => getMessage('verification', key),
		},

		// 네비게이션
		navigation: {
			menu: (key: string) => getNavigation(`menu.${key}`),
			breadcrumb: (key: string) => getNavigation(`breadcrumb.${key}`),
			menuItems: (key: number | string | undefined | null) =>
						getNavigation(`menuItems.${key}`),
			direct: (key: string) => getDirectText(key),
		},

		// 네비게이션
		menu: (key: string) => getCommonText('menu', key),
		breadcrumb: (key: string) => getCommonText('breadcrumb', key),

		hour: (hour: string | number) => getHourUnit(hour),
		minute: (minute: string | number) => getMinuteUnit(minute),

		user: getUserText, 			// 설정 > 회원관리
		menuAuth: getMenuText, 		// 설정 > 메뉴권한관리
		group: getGroupText, 		// 설정 > 그룹관리
		board: getBoardText, 		// 설정 > 공지관리
		terms: getTermsText, 		// 설정 > 약관관리
		log: getLogText, 			// 설정 > 로그관리
		facility: getFacilityText,	// 운영관리 > 현장관리
		terminal: getTerminalText,	// 운영관리 > 장비관리
		warranty: getASText,		// 운영관리 > A/S 관리
		faq: getFAQText,

		// 지도관련
		map: Object.assign(
			getMapText, // 기본 호출용 함수 (lang.map('a.b.c'))
			{
				button: {
					resetView: () => getMapText('buttons.resetView'),
					zoomIn: () => getMapText('buttons.zoomIn'),
					cluster: () => getMapText('buttons.cluster'),
				},
				type: (key: string) => getMapText(`types.${key}`),
				systemLabel: (type: string) => getMapText(`system.${type}`),
				facilityStatus: (key: string) => getMapText(`facilityStatus.${key}`),
				facilityFilter: (key: string) => getMapText(`facilityFilter.${key}`),
				installSummaryTitle: (type: string) => getMapText(`installSummary.title.${type}`),
				countryLabel: (key: string) => getMapText(`country.${key}`),
				searchPlaceholder: () => getMapText('searchPlaceholder'),
				title: () => getMapText('title'),
			}
		),

		// 상태 관련
		status: getStatusText,
		
		// 에러 메시지
		error: getErrorText,
		
		// SD카드 메시지
		sdCard: getSdCardText,
	}

	return {
		// vue-i18n 기본 함수들
		t,
		tm,
		locale, // 반응형 언어 설정
		langRadio,

		// UI 중심 헬퍼
		lang,

		// 개별 함수들
		getFormLabel: getLabel,
		getFormPlaceholder: getPlaceholder,
		getValidationMessage,
		getButtonText,
		getCommonText,
		getMessage,
		getHourUnit,
		getMinuteUnit
	};
}